/**
 * Tracking de session pour les analytics comportementales (chantier ES).
 *
 * - Un UUID v4 de session est conservé en sessionStorage avec un TTL GLISSANT
 *   de 30 minutes : renouvelé à chaque événement (= chaque appel API), régénéré
 *   si expiré. Envoyé sur tous les appels du proxy CS-Cart via le header
 *   `X-Pharme-Session` (voir fetchRest / pages/api/csCartProxy).
 * - Au clic sur un produit dans les résultats de recherche, {query, rank} est
 *   mémorisé ; si la fiche produit est chargée dans les 30 s, son fetch envoie
 *   `X-Pharme-Src: search` + `X-Pharme-Rank: <n>` (consommé une seule fois).
 * - Au clic sur une carte d'un bloc recommandé, c'est `X-Pharme-Src: recommend`, le rang
 *   et l'emplacement du bloc (`X-Pharme-Page`) qui partent avec ce même fetch.
 *
 * Tout est best-effort : sessionStorage indisponible (SSR, navigation privée
 * stricte...) => pas de headers, jamais d'erreur.
 */

const SESSION_STORAGE_KEY = 'pharme_session'
const CLICK_STORAGE_KEY = 'pharme_search_click'

/** TTL glissant de la session (30 min) */
const SESSION_TTL_MS = 30 * 60 * 1000

/** Fenêtre de validité d'un clic résultat de recherche -> fiche produit (30 s) */
const CLICK_TTL_MS = 30 * 1000

export const SESSION_HEADER = 'X-Pharme-Session'
export const RANK_HEADER = 'X-Pharme-Rank'
export const SOURCE_HEADER = 'X-Pharme-Src'
export const PAGE_HEADER = 'X-Pharme-Page'

const isBrowser = (): boolean => typeof window !== 'undefined'

const uuidv4 = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // Fallback (vieux navigateurs) : suffisant pour un identifiant de session
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/**
 * Retourne l'UUID de session courant (TTL glissant 30 min : renouvelé à chaque
 * appel, régénéré si expiré). Retourne null hors navigateur / storage bloqué.
 */
export const getPharmeSessionId = (): string | null => {
  if (!isBrowser()) return null
  try {
    const now = Date.now()
    let id: string | null = null

    const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as { id?: string; exp?: number }
      if (parsed?.id && typeof parsed.exp === 'number' && parsed.exp > now) {
        id = parsed.id
      }
    }

    if (!id) id = uuidv4()

    // TTL glissant : chaque événement repousse l'expiration
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({ id, exp: now + SESSION_TTL_MS }))
    return id
  } catch {
    return null
  }
}

/**
 * Mémorise le clic d'un produit dans les résultats de recherche.
 * @param query terme recherché
 * @param rank position (1-based) du produit dans les résultats
 */
export const recordSearchClick = (query: string, rank: number): void => {
  if (!isBrowser()) return
  try {
    window.sessionStorage.setItem(
      CLICK_STORAGE_KEY,
      JSON.stringify({ query, rank, ts: Date.now() })
    )
  } catch {
    // best-effort
  }
}

/**
 * Mémorise le clic sur une carte d'un bloc recommandé.
 * @param placement emplacement du bloc (ex. product:also-bought)
 * @param rank position (1-based) du produit dans le bloc
 */
export const recordRecommendClick = (placement: string, rank: number): void => {
  if (!isBrowser()) return
  try {
    window.sessionStorage.setItem(
      CLICK_STORAGE_KEY,
      JSON.stringify({ source: 'recommend', page: placement, rank, ts: Date.now() })
    )
  } catch {
    // best-effort
  }
}

/**
 * Consomme (une seule fois) le clic mémorisé : si un clic sur un résultat de recherche
 * ou un bloc recommandé a eu lieu il y a moins de 30 s, retourne les headers
 * X-Pharme-Src/X-Pharme-Rank/X-Pharme-Page à joindre au fetch de la fiche produit.
 * Sinon objet vide.
 */
export const consumeSearchClickHeaders = (): Record<string, string> => {
  if (!isBrowser()) return {}
  try {
    const raw = window.sessionStorage.getItem(CLICK_STORAGE_KEY)
    if (!raw) return {}
    window.sessionStorage.removeItem(CLICK_STORAGE_KEY)

    const parsed = JSON.parse(raw) as {
      source?: string
      page?: string
      query?: string
      rank?: number
      ts?: number
    }
    if (
      typeof parsed?.ts !== 'number' ||
      Date.now() - parsed.ts > CLICK_TTL_MS ||
      typeof parsed.rank !== 'number' ||
      parsed.rank <= 0
    ) {
      return {}
    }

    return {
      [SOURCE_HEADER]: parsed.source ?? 'search',
      [RANK_HEADER]: String(parsed.rank),
      ...(parsed.page ? { [PAGE_HEADER]: parsed.page } : {}),
    }
  } catch {
    return {}
  }
}
